import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import { useGetOnboardingChecklistQuery } from '@/api/api-hooks';
import { AppSkeletonLoader } from '@/components/AppSkeletonLoader';
import { useGetHasLibraryData } from '@/components/LibraryTable/useGetHasLibraryData';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { hasContextReady, useTelemetry } from '@/contexts/TelemetryContext';
import { useContextMode } from '@/hooks/useContextMode';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useIsOrgOrSourceAdmin } from '@/hooks/useIsOrgOrSourceAdmin';
import { useUser } from '@/hooks/useUser';

function OrganizationIndexComponent() {
  const navigate = useNavigate();
  const { organizationName } = Route.useParams();
  const { isMeLoading } = useUser();
  const { value: isSupportAgentEnabled } = useFeatureFlag(FEATURE_FLAGS.SUPPORT_AGENT);
  const isOrgOrSourceAdmin = useIsOrgOrSourceAdmin();
  // MCP Setup redirects away in LITE, so such an org without the support
  // agent lands on Sources instead of bouncing through it. The mode comes
  // from /me, which the redirect below already waits for.
  const isContextLight = useContextMode() === 'LITE';
  const { data: checklist, isError: isChecklistError } = useGetOnboardingChecklistQuery();
  const { data: telemetry, isError: isTelemetryError } = useTelemetry();
  // Telemetry lists codebases only; this says whether the org has any source
  // at all, so an org whose sources are PDFs is not kept on Sources forever.
  const {
    hasCodebaseAndPdfRecords,
    isError: isLibraryError,
    isLoading: isLibraryLoading,
  } = useGetHasLibraryData('codebaseAndPdf');

  useEffect(() => {
    // Wait for /me so admins aren't briefly treated as normal users
    // (isOrgOrSourceAdmin is false while it loads) and the context mode is
    // known, so a LITE org isn't sent through MCP Setup.
    if (isMeLoading) {
      return;
    }

    // Investigations is the home page wherever the support agent is on, for
    // every role and every stage of setup: its readiness panel says what the
    // org still needs, so nothing here has to decide that.
    if (isSupportAgentEnabled) {
      navigate({
        to: '/$organizationName/investigations',
        params: { organizationName },
        replace: true,
      });
      return;
    }

    // Users who don't administer the org or any source can only view (not
    // manage) their sources, so MCP Setup is their home page, or Codebases
    // in LITE context mode, which has no MCP.
    if (!isOrgOrSourceAdmin) {
      navigate({
        to: isContextLight ? '/$organizationName/sources' : '/$organizationName/mcp',
        params: { organizationName },
        replace: true,
      });
      return;
    }

    // MCP Setup is only worth landing on once there is context to query.
    // Until then the Sources page is where an admin connects a codebase and
    // watches it prepare: an org with no sources connects one there, and an
    // org whose codebases have no context yet watches them there. Telemetry
    // answers the second; while either is loading there is nothing to decide
    // yet, and if telemetry fails the checklist decides alone.
    if (isLibraryLoading || (!telemetry && !isTelemetryError)) {
      return;
    }
    const hasCodebases = (telemetry?.codebases.length ?? 0) > 0;
    const hasContextReadyCodebase = telemetry?.codebases.some(hasContextReady) ?? false;
    // A codebase in telemetry already proves the org has sources; the asset
    // query only matters when there is none, so its failing or lagging
    // cannot keep an org with ready context away from MCP Setup. When it
    // failed, "no records" means nothing, so the checklist decides alone
    // rather than Sources trapping an org whose sources are PDFs.
    const hasNoSources = !hasCodebases && !hasCodebaseAndPdfRecords && !isLibraryError;
    const staysOnSources = hasNoSources || (hasCodebases && !hasContextReadyCodebase);
    if (telemetry && staysOnSources) {
      navigate({
        to: '/$organizationName/sources',
        params: { organizationName },
        replace: true,
      });
      return;
    }

    if (checklist) {
      navigate({
        to:
          checklist.setup_mcp_completed_at || isContextLight ? '/$organizationName/sources' : '/$organizationName/mcp',
        params: { organizationName },
        replace: true,
      });
    } else if (isChecklistError) {
      // Fallback to sources if onboarding checklist fails to load
      navigate({
        to: '/$organizationName/sources',
        params: { organizationName },
        replace: true,
      });
    }
  }, [
    checklist,
    hasCodebaseAndPdfRecords,
    isChecklistError,
    isContextLight,
    isLibraryError,
    isLibraryLoading,
    isMeLoading,
    isOrgOrSourceAdmin,
    isSupportAgentEnabled,
    isTelemetryError,
    navigate,
    organizationName,
    telemetry,
  ]);

  return <AppSkeletonLoader />;
}

export const Route = createFileRoute('/$organizationName/')({
  component: OrganizationIndexComponent,
});
